/**
 * Booking-form money terms — a share of the headline figure, or a flat sum.
 *
 * Every commercial figure on the four booking forms was a DERIVED one: a
 * percentage of the selling price (OTP / ALS) or a multiple of the monthly
 * rental (OTR / ALR). Plenty of deals are agreed the other way round — "RM 5,000
 * earnest, full stop" — and an agent forced to quote that as a share is writing a
 * figure that silently re-prices itself the moment the price or the rent is
 * corrected. Each term now records how it was actually agreed.
 *
 * Contract — three `booking_forms.form_data` keys per term. No migration:
 * `form_data` is jsonb.
 *
 *   <term>_basis     'percent' | 'month' | 'amount'. **Absent ≡ the derived
 *                    basis for the form's sale type**, so every form written
 *                    before this option existed reads, and prints, exactly as it
 *                    always did.
 *   <term>_percent   SALES terms — the percentage of the selling price.
 *   <term>_month     RENT terms — the multiple of the monthly rental.
 *   <term>_rm        the flat ringgit figure, read ONLY when basis is 'amount'.
 *
 * Only the figure the basis names is saved — `pruneMoneyTerms` strips the other
 * one before the payload goes out, and both servers replace `form_data` wholesale
 * on update, so a switched basis can never leave a contradicting number behind
 * for the template to find.
 *
 * The same rule lives in both servers (`src/utils/bookingFormMoney.js`, kept
 * byte-identical by `npm run check:sync`) and in both front ends. All four must
 * agree: the screen quotes the figure, the template prints it, and an
 * agent-raised form and an HQ-raised one have to read the same.
 */

// The terms each sale type quotes as a derivation of its headline figure: SALES
// off the selling price, RENT off the monthly rental. `earnest_deposit` appears
// on both, quoted as a percentage on one and as months on the other.
export const SALES_MONEY_TERMS = ['earnest_deposit', 'balance_deposit', 'balance_purchase_price'];
export const RENT_MONEY_TERMS = ['advance_rental', 'security_deposit', 'utility_deposit', 'earnest_deposit'];
export const ALL_MONEY_TERMS = [
  'earnest_deposit', 'balance_deposit', 'balance_purchase_price',
  'advance_rental', 'security_deposit', 'utility_deposit',
];

export const isRentType = (saleType) => saleType === 'RENT';
/**
 * Extra payment lines — what this tenancy charges that the printed form does not.
 *
 * OTR and ALR each print a fixed payment list (advance rental, security and
 * utility deposits, and on the OTR a stamping fee), and a real tenancy routinely
 * carries more: an access-card deposit, a car-park rental, a renovation deposit.
 * With nowhere to put them they went into "other terms" as prose, which leaves
 * them out of the form's own TOTAL AMOUNT PAYABLE — so the total on a signed
 * document disagreed with what the tenant actually had to pay before keys.
 *
 * One `booking_forms.form_data` key holding an array; no migration, form_data is
 * jsonb. Each line quotes itself the same two ways a built-in rent term does:
 *
 *   label   what the line is called on the document
 *   basis   'amount' (the default — most added lines are a flat sum) | 'month'
 *   rm      the flat ringgit figure, read ONLY when basis is 'amount'
 *   month   the multiple of the monthly rental, read ONLY when basis is 'month'
 *
 * SALES forms quote none of these, so `pruneMoneyTerms` drops the key there —
 * the same rule it applies to a rent deposit left behind on a sales form.
 */
export const EXTRA_PAYMENTS_KEY = 'extra_payments';
export const MAX_EXTRA_PAYMENTS = 8;
export const EXTRA_PAYMENT_LABEL_MAX = 80;

/** A line the user added and never filled. It prints nothing, so it is not kept. */
export const isBlankExtraPayment = (item) => !String((item || {}).label || '').trim()
  && !Number((item || {}).rm) && !Number((item || {}).month);

/**
 * The saved lines, normalised: always an array, every row an object, no blanks,
 * capped in both count and label length. Every reader goes through this rather
 * than touching form_data, so a hand-edited or legacy payload cannot reach a
 * template as something other than a list of printable lines.
 */
export const extraPayments = (fd) => {
  const raw = (fd || {})[EXTRA_PAYMENTS_KEY];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item) && !isBlankExtraPayment(item))
    .slice(0, MAX_EXTRA_PAYMENTS)
    .map((item) => ({
      label: String(item.label || '').trim().slice(0, EXTRA_PAYMENT_LABEL_MAX),
      basis: item.basis === 'month' ? 'month' : 'amount',
      rm: Number(item.rm) || 0,
      month: Number(item.month) || 0,
    }));
};

/** What one line comes to in ringgit. `rental` is the monthly rental it may be a multiple of. */
export const extraPaymentAmount = (item, rental) => (
  (item || {}).basis === 'month'
    ? (Number(rental) || 0) * (Number((item || {}).month) || 0)
    : Number((item || {}).rm) || 0
);

/** A fresh, empty line for the "add" button — flat ringgit, the common case. */
export const blankExtraPayment = () => ({ label: '', basis: 'amount', rm: '' });


/** The basis a form of this sale type quoted on before the flat option existed. */
export const derivedBasis = (saleType) => (isRentType(saleType) ? 'month' : 'percent');

/** The money terms a form of this sale type quotes. */
export const moneyTerms = (saleType) => (isRentType(saleType) ? RENT_MONEY_TERMS : SALES_MONEY_TERMS);

export const basisKey = (term) => `${term}_basis`;
export const amountKey = (term) => `${term}_rm`;
export const derivedKey = (term, saleType) => `${term}_${isRentType(saleType) ? 'month' : 'percent'}`;

/**
 * How this term is quoted. Only an explicit 'amount' counts as flat — anything
 * else, including a missing key, is the derived basis this form has always used.
 */
export const moneyBasis = (fd, term, saleType) => (
  (fd || {})[basisKey(term)] === 'amount' ? 'amount' : derivedBasis(saleType)
);

export const isFlatAmount = (fd, term, saleType) => moneyBasis(fd, term, saleType) === 'amount';

/**
 * What the term comes to in ringgit. `base` is the figure the derived basis is a
 * share of — the selling price on a SALES form, the monthly rental on a RENT one.
 */
export const termAmount = (fd, term, saleType, base) => {
  const f = fd || {};
  if (isFlatAmount(f, term, saleType)) return Number(f[amountKey(term)]) || 0;
  const rate = Number(f[derivedKey(term, saleType)]) || 0;
  const headline = Number(base) || 0;
  return isRentType(saleType) ? headline * rate : (headline * rate) / 100;
};

/** Every money term's basis seeded to the one it has always used. */
export const defaultMoneyBasis = (saleType) => {
  const out = {};
  for (const term of moneyTerms(saleType)) out[basisKey(term)] = derivedBasis(saleType);
  return out;
};

/**
 * Drop every money figure a saved form must not carry: the alternative to the
 * basis each term names, and every term the other sale type quotes.
 *
 * Both apps keep what the agent typed on screen when they flip a basis back and
 * forth — deliberately, so nobody loses a figure to a stray tap. Without this,
 * that form_data would reach the template holding two contradicting numbers for
 * one line of a signed document.
 *
 * Returns a new object; the input is not mutated.
 */
export const pruneMoneyTerms = (fd, saleType) => {
  const out = { ...(fd || {}) };
  const quoted = new Set(moneyTerms(saleType));
  const otherType = isRentType(saleType) ? 'SALES' : 'RENT';

  for (const term of ALL_MONEY_TERMS) {
    if (!quoted.has(term)) {
      delete out[basisKey(term)];
      delete out[amountKey(term)];
      delete out[derivedKey(term, 'SALES')];
      delete out[derivedKey(term, 'RENT')];
      continue;
    }
    if (isFlatAmount(out, term, saleType)) delete out[derivedKey(term, saleType)];
    else delete out[amountKey(term)];
    // A term quoted by both sale types still only carries this form's unit.
    delete out[derivedKey(term, otherType)];
  }

  // The extra payment lines are a RENT-only list, and each keeps only the figure
  // its own basis names — the same rule as the terms above, for the same reason.
  if (!isRentType(saleType)) {
    delete out[EXTRA_PAYMENTS_KEY];
  } else {
    const lines = extraPayments(out).map(({ label, basis, rm, month }) => (
      basis === 'month' ? { label, basis, month } : { label, basis, rm }
    ));
    if (lines.length) out[EXTRA_PAYMENTS_KEY] = lines;
    else delete out[EXTRA_PAYMENTS_KEY];
  }
  return out;
};

// ── UI ───────────────────────────────────────────────────────────────────────
// Labels only past this point. The contract above is shared with both servers;
// what a control is called is each screen's own business.

/** The two-character unit that sits inside the field itself, as the basis toggle. */
export const moneyUnitOptions = (saleType) => (saleType === 'RENT'
  ? [{ value: 'month', label: 'mo' }, { value: 'amount', label: 'RM' }]
  : [{ value: 'percent', label: '%' }, { value: 'amount', label: 'RM' }]);

/** OTR, ALR and EL quote rent terms; OTP, ALS and ES quote sales terms. */
export const saleTypeOfCode = (code) => (code === 'OTR' || code === 'ALR' || code === 'EL' ? 'RENT' : 'SALES');

/**
 * The one figure this term currently asks for: its form_data key, and whether it
 * is the flat one. Every screen reads the pair from here so the input it mounts,
 * the label it wears and the key the submit check looks at cannot drift apart.
 */
export const moneyTermField = (fd, term, saleType) => {
  const flat = isFlatAmount(fd, term, saleType);
  return { key: flat ? amountKey(term) : derivedKey(term, saleType), flat };
};
